import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getEvent } from '../../firebase';
import { createEventCheckoutSession, getProduct } from '../../stripe';
import { Event, TicketAttendee } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const EventRegistration: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're in the portal or public route
  const isPortalRoute = location.pathname.startsWith('/portal');
  
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Purchaser and guests information
  const [purchaser, setPurchaser] = useState<TicketAttendee>({
    name: currentUser?.displayName || '',
    email: currentUser?.email || '',
    company: ''
  });
  const [guests, setGuests] = useState<TicketAttendee[]>([]);
  
  useEffect(() => {
    // Update purchaser info if currentUser changes
    if (currentUser) {
      setPurchaser(prev => ({
        ...prev,
        name: currentUser.displayName || prev.name,
        email: currentUser.email || prev.email
      }));
    }
  }, [currentUser]);
  
  useEffect(() => {
    const loadEvent = async () => {
      if (!eventId) {
        setError('Event ID is missing');
        setLoading(false);
        return;
      }
      
      try {
        const eventData = await getEvent(eventId);
        setEvent(eventData);
        
        // Check if event has a Stripe product set up
        const product = await getProduct(eventId);
        if (!product) {
          setError('This event is not available for registration');
        }
      } catch (err) {
        console.error('Failed to load event:', err);
        setError('Failed to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadEvent();
  }, [eventId]);
  
  const handlePurchaserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPurchaser(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleGuestChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGuests(prev => {
      const updatedGuests = [...prev];
      updatedGuests[index] = {
        ...updatedGuests[index],
        [name]: value
      };
      return updatedGuests;
    });
  };
  
  const addGuest = () => {
    setGuests(prev => [...prev, { name: '', email: '', company: '' }]);
  };
  
  const removeGuest = (index: number) => {
    setGuests(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleRegister = async () => {
    if (!event) {
      return;
    }
    
    // Validate required fields
    if (!purchaser.name || !purchaser.email) {
      setError('Please fill in your name and email');
      return;
    }
    
    // Validate guest information
    const invalidGuest = guests.findIndex(guest => !guest.name || !guest.email);
    if (invalidGuest !== -1) {
      setError(`Please fill in name and email for guest #${invalidGuest + 1}`);
      return;
    }
    
    try {
      setProcessingPayment(true);
      
      // Current URL for success/cancel redirects
      const baseUrl = window.location.origin;
      
      // Use the appropriate success URL based on where the user came from
      const successUrl = isPortalRoute
        ? `${baseUrl}/portal/events/registration-success?event=${event.id}`
        : `${baseUrl}/events/registration-success?event=${event.id}`;
      
      // Similarly for cancel URL
      const cancelUrl = isPortalRoute
        ? `${baseUrl}/portal/events/registration?event=${event.id}`
        : `${baseUrl}/events/registration?event=${event.id}`;
      
      // Total number of tickets (purchaser + guests)
      const quantity = 1 + guests.length;
      
      // Create checkout session with attendee information
      const checkoutUrl = await createEventCheckoutSession(
        event.id,
        'community-event',
        quantity,
        parseFloat(event.price || '0'),
        {
          eventTitle: event.title,
          purchaserName: purchaser.name,
          purchaserEmail: purchaser.email,
          purchaserCompany: purchaser.company || '',
          guestsInfo: JSON.stringify(guests),
          totalAttendees: quantity.toString(),
          userUID: currentUser?.uid || 'guest' // Include UID if available, otherwise mark as guest
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError('Failed to process registration. Please try again later.');
      setProcessingPayment(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg mb-4">{error}</div>
        <button 
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          onClick={() => navigate(isPortalRoute ? '/portal/events' : '/events')}
        >
          Back to Events
        </button>
      </div>
    );
  }
  
  if (!event) {
    return (
      <div className="text-center py-8">
        <div className="text-lg mb-4">Event not found</div>
        <button 
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          onClick={() => navigate(isPortalRoute ? '/portal/events' : '/events')}
        >
          Back to Events
        </button>
      </div>
    );
  }
  
  // Calculate total
  const pricePerTicket = parseFloat(event.price || '0');
  const totalQuantity = 1 + guests.length; // Purchaser + guests
  const totalPrice = (pricePerTicket * totalQuantity).toFixed(2);
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-3xl font-bold mb-6">{event.title} - Registration</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Event Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-600">Date:</p>
            <p className="font-medium">{event.date}</p>
          </div>
          <div>
            <p className="text-gray-600">Time:</p>
            <p className="font-medium">{event.time}</p>
          </div>
          <div>
            <p className="text-gray-600">Location:</p>
            <p className="font-medium">{event.location}</p>
          </div>
          <div>
            <p className="text-gray-600">Price per Ticket:</p>
            <p className="font-medium">${pricePerTicket.toFixed(2)}</p>
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-4">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Your Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-gray-700 text-sm font-medium mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={purchaser.name}
                  onChange={handlePurchaserChange}
                  disabled={processingPayment}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={purchaser.email}
                  onChange={handlePurchaserChange}
                  disabled={processingPayment}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="company" className="block text-gray-700 text-sm font-medium mb-1">
                  Company (optional)
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={purchaser.company}
                  onChange={handlePurchaserChange}
                  disabled={processingPayment}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
              
          {guests.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3">Guest Information</h3>
              {guests.map((guest, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">Guest #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeGuest(index)}
                      disabled={processingPayment}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor={`guest-${index}-name`} className="block text-gray-700 text-sm font-medium mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id={`guest-${index}-name`}
                        name="name"
                        value={guest.name}
                        onChange={(e) => handleGuestChange(index, e)}
                        disabled={processingPayment}
                        className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor={`guest-${index}-email`} className="block text-gray-700 text-sm font-medium mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id={`guest-${index}-email`}
                        name="email"
                        value={guest.email}
                        onChange={(e) => handleGuestChange(index, e)}
                        disabled={processingPayment}
                        className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor={`guest-${index}-company`} className="block text-gray-700 text-sm font-medium mb-1">
                        Company (optional)
                      </label>
                      <input
                        type="text"
                        id={`guest-${index}-company`}
                        name="company"
                        value={guest.company}
                        onChange={(e) => handleGuestChange(index, e)}
                        disabled={processingPayment}
                        className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
              
          <div className="mb-6">
            <button
              type="button"
              onClick={addGuest}
              disabled={processingPayment}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add Guest
            </button>
          </div>
              
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="flex justify-between mb-2">
              <span>Price per ticket:</span>
              <span>${pricePerTicket.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Number of tickets:</span>
              <span>{totalQuantity} ({guests.length > 0 ? `You + ${guests.length} guest${guests.length > 1 ? 's' : ''}` : 'You only'})</span>
            </div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-300 mt-2">
              <span>Total:</span>
              <span>${totalPrice}</span>
            </div>
          </div>
              
          <div className="flex flex-col md:flex-row justify-between">
            <button
              onClick={() => navigate(isPortalRoute ? '/portal/events' : '/events')}
              disabled={processingPayment}
              className="bg-gray-200 text-gray-800 px-6 py-3 rounded hover:bg-gray-300 mb-3 md:mb-0"
            >
              Cancel
            </button>
            <button
              onClick={handleRegister}
              disabled={processingPayment || event.status === 'closed'}
              className={`px-6 py-3 rounded ${
                processingPayment || event.status === 'closed'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {processingPayment ? 'Processing...' : 'Proceed to Payment'}
            </button>
          </div>
          
          {event.status === 'closed' && (
            <div className="mt-4 text-center text-red-600">
              This event is closed for registration.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventRegistration; 